/**
 * Generates a full color palette from a single hex and applies it as CSS
 * custom properties on :root.  Tailwind is configured to consume these
 * variables, so every `primary-*` utility updates instantly.
 *
 * See THEME.md for the design rationale.
 */

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) throw new Error(`Invalid hex color: ${hex}`);
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

function mix(base, target, weight) {
  return {
    r: Math.round(base.r + (target.r - base.r) * weight),
    g: Math.round(base.g + (target.g - base.g) * weight),
    b: Math.round(base.b + (target.b - base.b) * weight),
  };
}

function generatePalette(hex) {
  const base = hexToRgb(hex);
  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };

  return {
    50:  mix(base, white, 0.95),
    100: mix(base, white, 0.85),
    200: mix(base, white, 0.70),
    300: mix(base, white, 0.55),
    400: mix(base, white, 0.35),
    500: mix(base, white, 0.15),
    600: base,
    700: mix(base, black, 0.15),
    800: mix(base, black, 0.30),
    900: mix(base, black, 0.45),
    950: mix(base, black, 0.60),
  };
}

export function applyThemeColor(hex) {
  const palette = generatePalette(hex);
  const root = document.documentElement;

  for (const [shade, { r, g, b }] of Object.entries(palette)) {
    root.style.setProperty(`--color-primary-${shade}`, `${r} ${g} ${b}`);
  }
}

export function getPrimaryRgb(shade = 600) {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(`--color-primary-${shade}`)
    .trim();
  return raw || '22 74 121';
}

export function getPrimaryCss(shade = 600, alpha = 1) {
  const rgb = getPrimaryRgb(shade);
  return alpha < 1 ? `rgb(${rgb} / ${alpha})` : `rgb(${rgb})`;
}
